import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { User, UserCredential } from 'firebase/auth'
import { motion } from 'framer-motion'
import { AlertCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { GoogleLogo } from '../components/common/GoogleLogo'
import { useAuth } from '../context/auth/useAuth'
import { useToast } from '../context/toast/useToast'
import { setFavicon } from '../utils/setFavicon'
import { setPageTitle } from '../utils/setPageTitle'

export const Route = createFileRoute('/login')({
  component: () => <LoginPage />,
})

const LoginPage: React.FC = () => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const navigate = useNavigate()
  const { signInWithGoogle, user, loading: authLoading, error, clearError, logout } = useAuth()
  const { showToast } = useToast()

  const ALLOWED_DOMAINS = ['sofka.com.co', 'sofka.us']

  useEffect(() => {
    if (user && !authLoading && !isValidating) {
      navigate({ to: '/' })
    }
  }, [user, authLoading, isValidating, navigate])

  const validateEmailDomain = (email: string): boolean => {
    const domain = email.split('@')[1]
    return ALLOWED_DOMAINS.includes(domain)
  }

  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true)
      setIsValidating(true)
      clearError()

      const userCredential: UserCredential = await signInWithGoogle()
      const user: User = userCredential?.user

      if (user?.email && !validateEmailDomain(user.email)) {
        await logout()
        showToast({
          type: 'error',
          title: 'Dominio no válido',
          message: 'Solo se permiten correos corporativos de Sofka.',
        })
        return
      }

      showToast({
        type: 'success',
        title: 'Inicio de sesión exitoso',
        message: `¡Bienvenido, ${user?.displayName || user?.email}!`,
      })
      navigate({ to: '/' })
    } catch (err) {
      console.error('Google login error:', err)
    } finally {
      setIsValidating(false)
      setIsGoogleLoading(false)
    }
  }

  if (user && !isValidating) {
    navigate({ to: '/' })
    return null
  }

  setFavicon('/assets/primario_isotipo.png')
  setPageTitle('Sofka Agents')

  return (
    <div
      className="flex items-center justify-center min-h-screen 
      bg-neutral-950 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.12),_transparent_60%)] 
      p-4"
    >
      {/* Glow animado detrás */}
      <motion.div
        className="absolute top-1/3 left-1/2 w-[600px] h-[300px] -translate-x-1/2 rounded-full 
        bg-[radial-gradient(circle,_rgba(255,255,255,0.12),_transparent_70%)] blur-3xl opacity-40"
        animate={{ opacity: [0.25, 0.4, 0.25] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="w-full max-w-4xl mx-auto relative"
      >
        {/* Logo Sofka arriba del card */}
        <motion.div
          className="flex justify-center mb-4 absolute top-6 left-6 z-50"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <img
            src="/assets/LogoSofka_FondoNegro_med.png"
            alt="Logo Sofka"
            className="h-6 object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.3)] opacity-80"
          />
        </motion.div>

        <motion.div
          className="
            rounded-3xl bg-white/5 
            backdrop-blur-3xl shadow-[0_0_40px_rgba(0,0,0,0.6)]
            border border-white/10 
            overflow-hidden relative
          "
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5, ease: 'easeOut' }}
        >
          {/* Borde luminoso con "breathing" effect */}
          <motion.div
            className="absolute rounded-3xl pointer-events-none border border-white/20 shadow-[0_0_30px_rgba(255,255,255,0.2)]"
            animate={{ opacity: [0.15, 1, 0.15] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-10 md:p-14 text-center md:text-left relative">
            {/* Left Side - Logos */}
            <motion.div
              className="flex flex-col items-center justify-center space-y-5"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <img
                src="/assets/primario_isotipo.png"
                alt="Sofka Isotipo"
                className="size-40 object-contain drop-shadow-[0_0_25px_rgba(255,255,255,0.06)]"
              />

              <img src="/assets/logotipo.png" alt="Sofka Logo" className="w-52 object-contain opacity-90" />
            </motion.div>

            {/* Right Side */}
            <motion.div
              className="flex flex-col justify-center space-y-8"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { delay: 0.45, duration: 0.6, ease: 'easeOut' },
                },
              }}
            >
              <div>
                <h1
                  className="
                    text-[52px] font-extrabold leading-[1.15] pb-2
                    bg-linear-to-r from-primary-shell to-secondary-shell
                    bg-clip-text text-transparent
                    drop-shadow-[0_2px_6px_rgba(0,0,0,0.25)]
                  "
                >
                  Sofka Agents
                </h1>

                <p className="text-white text-md leading-relaxed opacity-90">
                  Tu hub centralizado de agentes IA especializados para arquitectura, estimación y diseño de
                  aplicaciones empresariales.
                </p>
              </div>

              {error && (
                <div className="p-4 bg-error-600/20 border border-error-800/10 rounded-lg flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-error shrink-0" />
                  <span className="text-error-200 text-sm">{error}</span>
                </div>
              )}

              {/* Google button */}
              <motion.button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading}
                className="
                  w-full bg-white/20 enabled:hover:bg-white/30
                  text-white font-semibold py-3.5 px-6 rounded-xl
                  border border-white/20 
                  shadow-[0_4px_16px_rgba(0,0,0,0.4)]
                  flex items-center justify-center gap-3
                  transition-all
                  disabled:opacity-50 disabled:cursor-default cursor-pointer
                  enabled:hover:scale-102 enabled:active:scale-98
                "
              >
                <div className="bg-white rounded-sm p-1">
                  <GoogleLogo />
                </div>
                <span>Continuar con Google</span>
              </motion.button>

              <p className="text-center text-white text-sm opacity-80">
                Solo correos corporativos de Sofka (@sofka.com.co, @sofka.us)
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          className="text-center mt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <p className="text-white/80 text-sm"> © Sofka {new Date().getFullYear()} - Todos los derechos reservados.</p>
        </motion.div>
      </motion.div>
    </div>
  )
}
